/**
 * SendConsultantRfpModal — "Request proposal" on a Subconsultant candidate (TeamSection.jsx).
 *
 * Three steps, so the request a consultant gets says what they need to price the work
 * (2026-10-27, after ESI/FME got a McMahon RFP with no scope at all):
 *   1. Request  — recipients (the candidate org's Contacts, Primary pre-checked), due date, the
 *                 services we are asking for (prefilled per Consultant Service from Supabase
 *                 consultant_service_scopes, editable for this one request), a note.
 *   2. Facts    — the project's scope of work (the title sheet's own wording, read only) and the
 *                 facts questions for this service (api/_rfpFacts.js). Answers save to the
 *                 PROJECT the moment you move on, so the next consultant's RFP starts filled in.
 *   3. Preview  — the actual PDF and email, stamped PREVIEW, and the drawing sheets that go with
 *                 it (latest posted revision per sheet, merged into one PDF on send).
 * Send runs api/consultant-rfps.js#createAndSendRfp, which freezes the PDF and emails it.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { getContactNamesByIds, getContactEmailsByIds } from '../services/contacts.js'
import { getRfpPrep, saveRfpFacts, previewConsultantRfp, sendConsultantRfp } from '../services/consultantRfps.js'
import { rfpQuestionsFor, rfpFactsMissing, ENERGY } from '../../../api/_rfpFacts.js'
import { loadPdfjs } from '../lib/pdfjs.js'

const FONT = '"DM Sans",sans-serif'
const label = { fontFamily: FONT, fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
const input = { width: '100%', boxSizing: 'border-box', fontFamily: FONT, fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: FONT, fontSize: 13, fontWeight: 500, cursor: 'pointer' }
const primaryBtn = (off) => ({ ...btnBase, background: '#07272D', color: '#fff', opacity: off ? 0.6 : 1, cursor: off ? 'default' : 'pointer' })
const quietBtn = { ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }
const muted = { fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.45 }
const sectionTitle = { fontFamily: FONT, fontSize: 12, fontWeight: 600, color: 'var(--aj-text)', margin: '4px 0 6px' }

const STEPS = ['Request', 'Project facts', 'Preview']

/** The preview PDF drawn page by page with pdf.js (the AlderQC loader) — an <iframe> leaves it to
 *  the browser's own PDF viewer, which some browsers and the app's own panes do not have. */
function PdfPages({ bytes }) {
  const host = useRef(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const pdfjs = await loadPdfjs()
        if (!pdfjs || !alive || !host.current) { setFailed(!pdfjs); return }
        const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise
        const width = host.current.clientWidth - 2
        host.current.innerHTML = ''
        for (let n = 1; n <= doc.numPages && alive; n++) {
          const page = await doc.getPage(n)
          const base = page.getViewport({ scale: 1 })
          const ratio = window.devicePixelRatio || 1
          const vp = page.getViewport({ scale: (width / base.width) * ratio })
          const canvas = document.createElement('canvas')
          canvas.width = vp.width; canvas.height = vp.height
          canvas.style.cssText = 'display:block;width:100%;margin:0 0 10px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,0.15)'
          host.current.appendChild(canvas)
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
        }
      } catch { if (alive) setFailed(true) }
    })()
    return () => { alive = false }
  }, [bytes])
  if (failed) return <div style={muted}>The preview could not be drawn here. Use Open in a new tab.</div>
  return <div ref={host} />
}

function OptionPills({ q, value, disabled, onPick }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {q.options.map(o => {
        const on = value === o.v
        return (
          <button key={o.v} type="button" disabled={disabled} onClick={() => onPick(on ? '' : o.v)}
            style={{ fontFamily: FONT, fontSize: 12, padding: '5px 11px', borderRadius: 20, cursor: disabled ? 'default' : 'pointer',
              border: '0.5px solid ' + (on ? 'var(--aj-accent)' : 'var(--aj-border-mid)'),
              background: on ? 'var(--aj-accent)' : 'transparent', color: on ? 'var(--aj-bg-surface)' : 'var(--aj-text)' }}>
            {o.l}
          </button>
        )
      })}
    </div>
  )
}

/** What drawings go with the request: the latest POSTED revision of each project sheet (the
 *  Reference > Drawing Sheets set), merged into one PDF on send. `listed` shows every sheet. */
function DrawingsNote({ drawings, listed }) {
  if (!drawings) return null
  const inSet = drawings.filter(d => d.included)
  const left = drawings.filter(d => !d.included)
  if (!drawings.length) {
    return <div style={{ ...muted, color: '#b26a00' }}>No drawings go with this request: the project has no posted drawing sheets yet.</div>
  }
  return (
    <div>
      <div style={sectionTitle}>Drawings</div>
      <div style={muted}>
        The latest posted drawings, {inSet.length} sheet{inSet.length === 1 ? '' : 's'}, are merged into one PDF and attached
        (sent as a download link instead if the set is over 25 MB).
      </div>
      {left.length > 0 && (
        <div style={{ ...muted, color: '#b26a00', marginTop: 4 }}>
          Left out, no PDF on its latest posted revision: {left.map(d => d.number).join(', ')}
        </div>
      )}
      {listed && (
        <div style={{ marginTop: 6, columns: 2, columnGap: 18 }}>
          {inSet.map(d => (
            <div key={d.number + d.title} style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text)', breakInside: 'avoid' }}>
              <span style={{ fontFamily: '"DM Mono",monospace', marginRight: 6 }}>{d.number}</span>{d.title}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function SendConsultantRfpModal({ projectId, projectConsultantId, org, consultantService, onClose, onSent }) {
  const [step, setStep] = useState(0)
  const [names, setNames] = useState({})
  const [emails, setEmails] = useState({})
  const [checked, setChecked] = useState({})
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')
  const [services, setServices] = useState('')
  const [facts, setFacts] = useState({})
  const [prep, setPrep] = useState(null)          // null = loading, else { scope, project, ... }
  const [preview, setPreview] = useState(null)    // { url, email }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const contactIds = (org && org.fields['Contacts']) || []
  const primaryContactId = org && org.fields['Primary Contact'] && org.fields['Primary Contact'][0]
  const senderName = ctx.currentMember?.fields?.['Member Name'] || ''
  const senderEmail = ctx.currentMember?.fields?.['Member Email'] || ''

  useEffect(() => {
    if (!contactIds.length) return
    getContactNamesByIds(contactIds).then(setNames)
    getContactEmailsByIds(contactIds).then(setEmails)
    setChecked(primaryContactId ? { [primaryContactId]: true } : {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org && org.id])

  useEffect(() => {
    if (!projectId) return
    let alive = true
    getRfpPrep(projectId, consultantService).then(p => {
      if (!alive) return
      setPrep(p)
      setServices(p.services || '')
      const f = { ...(p.facts || {}) }
      if (!f.work_type && p.suggestedWorkType) f.work_type = p.suggestedWorkType
      setFacts(f)
    }).catch(e => { if (alive) { setPrep({ scope: { groups: [] }, failed: true }); setError((e && e.message) || 'Could not load the project facts.') } })
    return () => { alive = false }
  }, [projectId, consultantService])

  // the preview's blob URL is released when it is replaced or the window closes
  useEffect(() => () => { if (preview && preview.url) URL.revokeObjectURL(preview.url) }, [preview])

  const questions = useMemo(() => rfpQuestionsFor(consultantService, facts), [consultantService, facts])
  const missing = useMemo(() => rfpFactsMissing(consultantService, facts), [consultantService, facts])

  if (!projectId || !org) return null

  const orgName = org.fields['Organization Name'] || ''
  const selectedIds = Object.keys(checked).filter(id => checked[id])
  const payload = () => ({
    projectId, projectConsultantId, orgAirtableId: org.id, orgName, consultantService,
    dueDate: dueDate || null, notes: notes.trim() || null, facts, services,
    recipients: selectedIds.map(id => ({ contactAirtableId: id, name: names[id] || '', email: emails[id] || '' })),
    senderName, senderEmail,
  })

  function setFact(key, v) { setFacts(f => ({ ...f, [key]: v })) }

  async function toFacts() {
    if (!selectedIds.length) { setError('Pick at least one recipient.'); return }
    setError(''); setStep(1)
  }

  async function toPreview() {
    setBusy(true); setError('')
    try {
      await saveRfpFacts(projectId, facts, senderName)
      const p = await previewConsultantRfp(payload())
      const bin = atob(p.pdfBase64)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      setPreview({ url, bytes, email: p.email, drawings: p.drawings || [] })
      setStep(2)
    } catch (e) {
      setError((e && e.message) || 'Could not build the preview.')
    }
    setBusy(false)
  }

  async function send() {
    setBusy(true); setError('')
    try {
      await sendConsultantRfp(payload())
      onSent()
    } catch (e) {
      setError((e && e.message) || 'Could not send the request.')
      setBusy(false)
    }
  }

  const groups = (prep && prep.scope && prep.scope.groups) || []
  const sentTo = selectedIds.map(id => (names[id] || '…') + (emails[id] ? ' <' + emails[id] + '>' : ' (no email)')).join(', ')

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="SendConsultantRfpModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: step === 2 ? 760 : 600, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>Request proposal — {orgName}</div>
            <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
              {STEPS.map((s, i) => (
                <span key={s} style={{ fontFamily: FONT, fontSize: 11, color: i === step ? 'var(--aj-text)' : 'var(--aj-text-muted)', fontWeight: i === step ? 600 : 400 }}>
                  <span style={{ fontFamily: '"DM Mono",monospace' }}>{i + 1}</span> {s}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} disabled={busy} style={{ background: 'transparent', border: 'none', cursor: busy ? 'default' : 'pointer', color: 'var(--aj-text-muted)', padding: 4, opacity: busy ? 0.5 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0 }}>
          {step === 0 && (<>
            <div>
              <label style={label}>Send to</label>
              {contactIds.length === 0 && (
                <div style={{ ...muted, fontSize: 12.5 }}>{orgName} has no people in Contacts yet — add them there first.</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 160, overflowY: 'auto' }}>
                {contactIds.map(cid => (
                  <label key={cid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!checked[cid]} onChange={() => setChecked(c => ({ ...c, [cid]: !c[cid] }))} />
                    <span style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text)' }}>{names[cid] || '…'}</span>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)' }}>{emails[cid] || 'no email'}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label style={label}>Proposal due</label>
              <input type="date" style={input} value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div>
              <label style={label}>Services requested{consultantService ? ' — ' + consultantService : ''}</label>
              <textarea style={{ ...input, minHeight: 110, resize: 'vertical' }} value={services} onChange={e => setServices(e.target.value)}
                placeholder={prep ? 'One service per line' : 'Loading the standard services…'} />
              <div style={{ ...muted, marginTop: 4 }}>One per line. Changes here apply to this request only.</div>
            </div>
            <div>
              <label style={label}>Note for this request (optional)</label>
              <textarea style={{ ...input, minHeight: 60, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Anything specific to this request, e.g. what the city asked for" />
            </div>
            <DrawingsNote drawings={prep && prep.drawings} />
          </>)}

          {step === 1 && (<>
            <div>
              <div style={sectionTitle}>Scope of work</div>
              {!prep && <div style={muted}>Loading…</div>}
              {prep && groups.length === 0 && (
                <div style={{ ...muted, color: '#b26a00' }}>
                  No scope of work yet. It comes from the Selected Concept's areas (the same data as the G001 title sheet). Fill those in, or describe the work in the note.
                </div>
              )}
              {groups.map(g => (
                <div key={g.title} style={{ marginBottom: 6 }}>
                  <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 500, color: 'var(--aj-text-mid)' }}>{g.title}</div>
                  {g.lines.map(l => <div key={l} style={{ fontFamily: FONT, fontSize: 12.5, color: 'var(--aj-text)', paddingLeft: 10 }}>{l}</div>)}
                </div>
              ))}
              {groups.length > 0 && <div style={muted}>From the title sheet. To change it, edit the concept's areas.</div>}
            </div>

            <div>
              <div style={sectionTitle}>Project facts</div>
              <div style={{ ...muted, marginBottom: 8 }}>Saved on the project, so the next consultant's request starts with these answers.</div>
              {ENERGY.includes(consultantService) && prep && prep.scope && (prep.scope.record || []).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {prep.scope.record.map(r => (
                    <div key={r.label} style={{ fontFamily: FONT, fontSize: 12.5, color: 'var(--aj-text)' }}>
                      <span style={{ color: 'var(--aj-text-muted)' }}>{r.label}:</span> {r.answer}
                    </div>
                  ))}
                  <div style={{ ...muted, marginTop: 2 }}>From the project record. Glazing and assemblies go with the drawings.</div>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {questions.map(q => (
                  <div key={q.key}>
                    <label style={label}>{q.label}</label>
                    {q.kind === 'text'
                      ? <input style={input} value={facts[q.key] || ''} placeholder={q.placeholder || ''} onChange={e => setFact(q.key, e.target.value)} />
                      : <OptionPills q={q} value={facts[q.key] || ''} onPick={v => setFact(q.key, v)} />}
                  </div>
                ))}
              </div>
            </div>
          </>)}

          {step === 2 && preview && (<>
            <div style={{ ...muted, fontSize: 12.5 }}>
              <div><b style={{ color: 'var(--aj-text-mid)', fontWeight: 500 }}>To:</b> {sentTo}</div>
              <div><b style={{ color: 'var(--aj-text-mid)', fontWeight: 500 }}>Subject:</b> {preview.email.subject}</div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 6, color: 'var(--aj-text)' }}>{preview.email.text}</div>
              <div style={{ marginTop: 4 }}>
                Attached: the request below (the PREVIEW stamp is left off what is sent).{' '}
                <a href={preview.url} target="_blank" rel="noreferrer" style={{ color: 'var(--aj-accent)' }}>Open in a new tab</a>
              </div>
            </div>
            <DrawingsNote drawings={preview.drawings} listed />
            <div style={{ background: 'var(--aj-bg-muted)', borderRadius: 4, padding: 12 }}>
              <PdfPages bytes={preview.bytes} />
            </div>
          </>)}

          {error && <div style={{ fontFamily: FONT, fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1, ...muted }}>
            {step === 1 && missing.length > 0 && `${missing.length} not answered — they will print as "To be confirmed".`}
          </div>
          {step === 0 && <>
            <button onClick={onClose} style={quietBtn}>Cancel</button>
            <button onClick={toFacts} disabled={!selectedIds.length || !prep} style={primaryBtn(!selectedIds.length || !prep)}>Next: project facts</button>
          </>}
          {step === 1 && <>
            <button onClick={() => setStep(0)} disabled={busy} style={quietBtn}>Back</button>
            <button onClick={toPreview} disabled={busy} style={primaryBtn(busy)}>{busy ? 'Building preview…' : 'Preview request'}</button>
          </>}
          {step === 2 && <>
            <button onClick={() => setStep(1)} disabled={busy} style={quietBtn}>Back to edit</button>
            <button onClick={send} disabled={busy} style={primaryBtn(busy)}>{busy ? 'Assembling drawings and sending…' : 'Send to ' + (selectedIds.length === 1 ? (names[selectedIds[0]] || '1 person') : selectedIds.length + ' people')}</button>
          </>}
        </div>
      </div>
    </div>,
    document.body
  )
}
